import '@theme/spx-slider';

class SpxDayCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.slider = this.querySelector('spx-slider');
    this.originalSlides = [...this.querySelectorAll('.spx-day-carousel__slide:not([data-day-clone])')]
      .sort((a, b) => Number(a.dataset.dayIndex) - Number(b.dataset.dayIndex));
    this.copy = this.originalSlides.flatMap((slide) => [...slide.querySelectorAll('[data-day-copy]')]);
    this.addEventListener('spx:before-slider-init', this.prepareLoop, { signal });
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleMeasure();
      this.scheduleLoopCheck();
    });
    this.copy.forEach((copy) => this.resizeObserver.observe(copy));
    if (this.slider) this.resizeObserver.observe(this.slider);
    window.addEventListener('resize', this.scheduleLoopCheck, { signal });
    this.scheduleMeasure();

    this.addEventListener('spx:slider-ready', this.handleSliderReady, { signal });
    this.addEventListener('spx:slide-change', this.syncPagination, { signal });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-day-page]');
      if (button) this.goToScene(Number(button.dataset.dayPage));
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const slide = event.target.closest('.spx-day-carousel__slide');
      if (!slide || slide.hasAttribute('data-day-clone')) return;
      this.querySelector('spx-slider')?.swiperInstance?.autoplay?.stop();
      this.goToScene(Number(slide.dataset.dayIndex), 0);
    }, { signal });
    // Release the shared slider only after the buffer hook is registered.
    this.slider?.removeAttribute('data-defer-init');
    this.initFrame = requestAnimationFrame(() => {
      this.initFrame = 0;
      if (!this.slider?.isConnected) return;
      if (this.slider.swiperInstance) this.rebuildLoop();
      else this.slider.syncSwiperWithViewport();
    });
  }

  getLoopPlan(config) {
    const count = this.originalSlides.length;
    if (!config.loop || count < 2) return { sets: 1, additional: 0, key: 'static' };
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const perView = desktop ? config.slidesPerViewDesktop : config.slidesPerViewMobile;
    const gap = desktop ? config.spaceBetweenDesktop : config.spaceBetweenMobile;
    const group = desktop ? config.slidesPerGroupDesktop : config.slidesPerGroupMobile;
    const container = this.slider.querySelector('.swiper');
    const bounds = container.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const section = this.getBoundingClientRect();
    const visibleLeft = Math.max(0, section.left);
    const visibleRight = Math.min(viewportWidth, section.right);
    const spill = Math.max(0, bounds.left - visibleLeft, visibleRight - bounds.right);
    const step = ((container.clientWidth || viewportWidth) + gap) / perView;
    // Reserve the cards visible outside the Swiper viewport, plus its move group.
    const additional = Math.max(1, Math.ceil(spill / step));
    const minimum = Math.ceil(perView) + 2 * (additional + group);
    const sets = Math.ceil(minimum / count);
    return { sets, additional, key: `${sets}:${additional}` };
  }

  prepareLoop = (event) => {
    if (event.target !== this.slider) return;
    const { config, container } = event.detail;
    const plan = this.getLoopPlan(config);
    const wrapper = container.querySelector('.swiper-wrapper');
    wrapper.querySelectorAll('[data-day-clone]').forEach((slide) => slide.remove());
    wrapper.append(...this.originalSlides);
    const copies = document.createDocumentFragment();
    for (let set = 1; set < plan.sets; set++) {
      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        if (!(clone instanceof HTMLElement)) return;
        clone.setAttribute('data-day-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        [clone, ...clone.querySelectorAll('*')].forEach((element) => {
          element.removeAttribute('id');
          element.removeAttribute('data-day-copy');
          [...element.attributes].forEach((attribute) => {
            if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
          });
        });
        // Copies keep their links clickable without repeating hidden items in keyboard navigation.
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        copies.append(clone);
      });
    }
    wrapper.append(copies);
    config.loopAdditionalSlides = plan.additional;
    if (this.pendingScene !== undefined) config.initialSlide = this.pendingScene;
    this.loopPlanKey = plan.key;
  };

  handleSliderReady = () => {
    const swiper = this.slider?.swiperInstance;
    swiper?.off('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
    if (swiper?.params.loop) {
      swiper.on('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
      this.trackInitialClip();
    } else {
      cancelAnimationFrame(this.clipFrame);
      this.clipFrame = 0;
      this.slider?.removeAttribute('data-day-initial');
      this.slider?.style.removeProperty('--day-initial-clip');
    }
    this.pendingScene = undefined;
    this.syncPagination();
    this.scheduleLoopCheck();
  };

  trackInitialClip = () => {
    if (this.clipFrame || !this.slider?.swiperInstance?.params.loop || !this.slider.hasAttribute('data-day-initial')) return;
    this.clipFrame = requestAnimationFrame(this.updateInitialClip);
  };

  updateInitialClip = () => {
    this.clipFrame = 0;
    const swiper = this.slider?.swiperInstance;
    const first = this.originalSlides[0];
    if (!swiper?.params.loop || !first || !this.slider.hasAttribute('data-day-initial')) return;
    const edge = first.getBoundingClientRect().left;
    const visibleLeft = Math.max(0, this.getBoundingClientRect().left);
    if (edge <= visibleLeft + 0.5) {
      // Its predecessor is now fully offscreen, so removing the mask reveals no card.
      this.slider.removeAttribute('data-day-initial');
      this.slider.style.removeProperty('--day-initial-clip');
      return;
    }
    const origin = this.slider.querySelector('.swiper').getBoundingClientRect().left;
    this.slider.style.setProperty('--day-initial-clip', `${Math.min(0, edge - origin)}px`);
    // Follow the rendered position throughout both a drag and its settling transition.
    if (swiper.animating || swiper.touchEventsData?.isTouched) this.trackInitialClip();
  };

  rebuildLoop() {
    const swiper = this.slider?.swiperInstance;
    if (!swiper) return;
    this.pendingScene = Number(swiper.slides[swiper.activeIndex]?.dataset.dayIndex) || 0;
    this.slider.destroySwiper();
    this.slider.initSwiper();
  }

  scheduleLoopCheck = () => {
    if (this.loopFrame) return;
    this.loopFrame = requestAnimationFrame(() => {
      this.loopFrame = 0;
      if (!this.isConnected || !this.slider?.swiperInstance) return;
      const plan = this.getLoopPlan(this.slider.getConfig());
      if (plan.key !== this.loopPlanKey) this.rebuildLoop();
    });
  };

  goToScene(index, speed) {
    const swiper = this.querySelector('spx-slider')?.swiperInstance;
    if (!swiper || !Number.isInteger(index)) return;
    const candidates = [...swiper.slides].filter((slide) => Number(slide.dataset.dayIndex) === index);
    if (candidates.length === 0) return;
    if (!swiper.params.loop) {
      swiper.slideTo([...swiper.slides].indexOf(candidates[0]), speed);
      return;
    }

    // Select the nearest occurrence, including across the loop boundary.
    const count = swiper.slides.length;
    const distance = (slide) => {
      const delta = Math.abs(Number(slide.dataset.swiperSlideIndex) - swiper.realIndex);
      return Math.min(delta, count - delta);
    };
    candidates.sort((a, b) => distance(a) - distance(b));
    swiper.slideToLoop(Number(candidates[0].dataset.swiperSlideIndex), speed);
  }

  syncPagination = () => {
    const swiper = this.querySelector('spx-slider')?.swiperInstance;
    if (!swiper) return;
    const index = Number(swiper.slides[swiper.activeIndex]?.dataset.dayIndex);
    this.querySelectorAll('[data-day-page]').forEach((button) => {
      const active = Number(button.dataset.dayPage) === index;
      button.classList.toggle('swiper-pagination-bullet-active', active);
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  };

  disconnectedCallback() {
    this.slider?.swiperInstance?.off('setTranslate sliderFirstMove transitionStart', this.trackInitialClip);
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = 0;
    cancelAnimationFrame(this.loopFrame);
    this.loopFrame = 0;
    cancelAnimationFrame(this.initFrame);
    this.initFrame = 0;
    cancelAnimationFrame(this.clipFrame);
    this.clipFrame = 0;
  }

  scheduleMeasure = () => {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = 0;
      // Share the tallest natural copy height so alternating cards keep aligned outer edges.
      const height = Math.ceil(Math.max(0, ...this.copy.map((copy) => copy.getBoundingClientRect().height)));
      this.style.setProperty('--day-copy-height', `${height}px`);
    });
  };
}

if (!customElements.get('spx-day-carousel')) {
  customElements.define('spx-day-carousel', SpxDayCarousel);
}
